import { useRef, useState } from "react";
import { partyTheme } from "../pages/accountDetail/accountDetailTheme";

// Component to render claim text with interactive references
function ClaimText({
  text,
  claimType,
  claimLookup = {},
  patentLabel = null,
  onClaimReferenceClick,
}) {
  const [hoveredClaim, setHoveredClaim] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const popupRef = useRef(null);

  // Parse claim text and identify references like "claim 1", "claim 10", etc.
  const parseClaimReferences = (claimText) => {
    // Match patterns like "claim 1", "claim 10", "claims 1-5", etc.
    const claimRefPattern = /(claim(?:s)?\s+\d+(?:\s*-\s*\d+)?)/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    const regex = new RegExp(claimRefPattern);
    while ((match = regex.exec(claimText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: claimText.substring(lastIndex, match.index),
        });
      }

      // Add the claim reference
      parts.push({
        type: "claim-ref",
        content: match[0],
        claimNumber: match[0].match(/\d+/)[0], // Extract the claim number
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < claimText.length) {
      parts.push({
        type: "text",
        content: claimText.substring(lastIndex),
      });
    }

    return parts;
  };

  const handleClaimHover = (claimNumber, event) => {
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5,
    });
    setHoveredClaim(claimNumber);
  };

  const handleClaimLeave = () => {
    setHoveredClaim(null);
  };

  const parts = parseClaimReferences(text);

  return (
    <div style={{ position: "relative" }}>
      {parts.map((part, index) => {
        if (part.type === "text") {
          return <span key={index}>{part.content}</span>;
        } else {
          return (
            <span
              key={index}
              onMouseEnter={(e) => handleClaimHover(part.claimNumber, e)}
              onMouseLeave={handleClaimLeave}
              onClick={() => onClaimReferenceClick?.(part.claimNumber)}
              style={{
                color:
                  claimType === "yours"
                    ? partyTheme.yours.textLabel
                    : partyTheme.theirs.textLabel,
                fontWeight: 600,
                textDecoration: "underline",
                textDecorationStyle: "dotted",
                cursor: "pointer",
                position: "relative",
              }}
            >
              {part.content}
            </span>
          );
        }
      })}

      {/* Popup tooltip */}
      {hoveredClaim && (
        <div
          ref={popupRef}
          style={{
            position: "fixed",
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            background: "white",
            border: `1px solid ${
              claimType === "yours"
                ? partyTheme.yours.border
                : partyTheme.theirs.border
            }`,
            borderRadius: 0,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "12px 16px",
            zIndex: 1000,
            maxWidth: "400px",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color:
                claimType === "yours"
                  ? partyTheme.yours.textLabel
                  : partyTheme.theirs.textLabel,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: "4px",
              lineHeight: 1.25,
            }}
          >
            Referenced Claim {hoveredClaim}
          </div>
          {patentLabel && (
            <div style={{ marginBottom: "6px" }}>
              {patentLabel.includes(" · ") ? (
                <>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      lineHeight: 1.3,
                      color: "#3d3d3a",
                    }}
                  >
                    {patentLabel.split(" · ")[0]}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      lineHeight: 1.25,
                      color: "#7a756c",
                      fontFamily: "ui-monospace, monospace",
                      marginTop: "2px",
                    }}
                  >
                    {patentLabel.split(" · ").slice(1).join(" · ")}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    fontSize: "10px",
                    lineHeight: 1.25,
                    color: "#7a756c",
                  }}
                >
                  {patentLabel}
                </div>
              )}
            </div>
          )}
          <div
            style={{
              fontSize: "12px",
              lineHeight: 1.45,
              color: "#333",
            }}
          >
            {claimLookup[hoveredClaim] ? (
              <span>{claimLookup[hoveredClaim]}</span>
            ) : (
              <em style={{ color: "#666" }}>
                Referenced claim text is not available for this patent yet.
              </em>
            )}
          </div>
          <div
            style={{
              marginTop: "6px",
              paddingTop: "6px",
              borderTop: "1px solid #e5e5e5",
              fontSize: "10px",
              lineHeight: 1.35,
              color: "#999",
            }}
          >
            Claim dependencies help establish patent scope
          </div>
        </div>
      )}
    </div>
  );
}

export { ClaimText };
